/**
 * Profile-key primitive — Signal's per-user 32-byte secret that gates
 * access to the user's profile blob (name, avatar, about, emoji status).
 *
 * The server stores only the AES-GCM ciphertext of the blob keyed by
 * userId; without the profile key, an attacker that controls the server
 * learns nothing about the contents. The key is distinct from the
 * identity key so its distribution policy ("only contacts I have shared
 * with") is decoupled from message-routing identity. Distribution happens
 * over the existing pairwise E2EE channel; the server never sees the key
 * itself.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ProfileKey.swift` byte-for-byte.
 */

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class ProfileKeyError extends Error {
  constructor(
    public readonly kind:
      | "invalidKeyLength"
      | "sealFailure"
      | "openFailure"
      | "malformedBlob"
      | "unsupportedSchema",
    message: string,
  ) {
    super(message);
    this.name = "ProfileKeyError";
  }
}

// ---------------------------------------------------------------------------
// ProfileKey
// ---------------------------------------------------------------------------

/**
 * 32-byte secret known to the owner and to contacts the owner has
 * shared their profile with. The server never sees this value. Used as
 * input keying material for HKDF; downstream cipher / domain keys are
 * derived via distinct info strings so the same raw key cannot be
 * repurposed across features.
 */
export class ProfileKey {
  /** The raw 32-byte key material. */
  readonly raw: Uint8Array;

  constructor(raw: Uint8Array) {
    if (raw.byteLength !== 32) {
      throw new ProfileKeyError(
        "invalidKeyLength",
        `ProfileKey must be 32 bytes, got ${raw.byteLength}`,
      );
    }
    // Copy to own buffer — prevents the caller from mutating our internals.
    this.raw = raw.slice();
  }

  /** Cryptographically-secure 32-byte random key via crypto.getRandomValues. */
  static generate(): ProfileKey {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return new ProfileKey(bytes);
  }

  /** Byte-level equality. */
  equals(other: ProfileKey): boolean {
    if (this.raw.byteLength !== other.raw.byteLength) return false;
    let diff = 0;
    for (let i = 0; i < this.raw.byteLength; i++) {
      diff |= this.raw[i] ^ other.raw[i];
    }
    return diff === 0;
  }

  /** Serialise to a plain base64 string (for JSON payloads). */
  toBase64(): string {
    return btoa(String.fromCharCode(...this.raw));
  }

  /** Deserialise from a plain base64 string. */
  static fromBase64(b64: string): ProfileKey {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new ProfileKey(bytes);
  }
}
